// Domain types for image annotation entities.
// Coordinates are stored in un-rotated original image pixel space
// (all x/y/width/height/radius/points[] are relative to widthOriginal x heightOriginal).

import type { Measurements, Position } from '@interfaces/base';

export enum AnnotationType {
  Pen = 'pen',
  Highlighter = 'highlighter',
  Arrow = 'arrow',
  Rectangle = 'rectangle',
  Circle = 'circle',
  Text = 'text',
}

export type AnnotationPoint = Position;

export interface AnnotationStyle {
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
}

export interface AnnotationBase {
  id: string;
  type: AnnotationType;
  documentId: number;
  pageNumber: number;
  style: AnnotationStyle;
  createdBy: number;
  createdAt: string;
  modifiedBy: number;
  modifiedAt: string;
}

export interface PenAnnotation extends AnnotationBase {
  type: AnnotationType.Pen;
  points: AnnotationPoint[];
}

export interface HighlighterAnnotation extends AnnotationBase {
  type: AnnotationType.Highlighter;
  points: AnnotationPoint[];
}

export interface ArrowAnnotation extends AnnotationBase {
  type: AnnotationType.Arrow;
  start: AnnotationPoint;
  end: AnnotationPoint;
}

export interface RectangleAnnotation extends AnnotationBase, Measurements {
  type: AnnotationType.Rectangle;
  origin: AnnotationPoint;
  filled?: boolean;
}

export interface CircleAnnotation extends AnnotationBase {
  type: AnnotationType.Circle;
  center: AnnotationPoint;
  radiusX: number;
  radiusY: number;
  filled?: boolean;
}

export interface TextAnnotation extends AnnotationBase {
  type: AnnotationType.Text;
  position: AnnotationPoint;
  text: string;
  fontSize: number;
  fontFamily?: string;
}

export type Annotation =
  | PenAnnotation
  | HighlighterAnnotation
  | ArrowAnnotation
  | RectangleAnnotation
  | CircleAnnotation
  | TextAnnotation;
